// Plan v7 / Day 24 F7 — LangSwitcher dropdown component.
//
// 5-locale dropdown rendered in the main navbar (desktop) and the mobile
// hamburger menu. Hidden entirely when only ONE locale is active server-side
// (production safety: prevents users from picking an inactive locale that
// the SetLocale middleware would 301 back to default).

import clsx from 'clsx';
import {Menu, MenuTrigger} from '@ui/menu/menu-trigger';
import {Item} from '@ui/forms/listbox/item';
import {Button} from '@ui/buttons/button';
import {IconButton} from '@ui/buttons/icon-button';
import {LanguageIcon} from '@ui/icons/material/Language';
import {LOCALE_META, type AppLocale} from './locale-config';
import {useLocale} from './use-locale';

interface Props {
  /**
   * Visual variant:
   *   'navbar'  — compact button with flag+label, used inside the desktop top bar
   *   'compact' — icon-only button (mobile fallback)
   *
   * The mobile hamburger menu uses inline `<Item>` elements rendered directly
   * inside the parent `<Menu>` to keep keyboard/selection wiring intact —
   * not this component. See `main-navbar.tsx` mobile section.
   */
  variant?: 'navbar' | 'compact';
  className?: string;
}

export function LangSwitcher({variant = 'navbar', className}: Props) {
  const {current, active, switchTo} = useLocale();

  // Production safety: only render the switcher when at least 2 locales are
  // active. If a deploy has I18N_ACTIVE_LOCALES=tr only, nothing is shown.
  if (active.length < 2) {
    return null;
  }

  const currentMeta = LOCALE_META[current];

  const trigger =
    variant === 'compact' ? (
      <IconButton
        className={clsx(
          'h-38 w-38 rounded-full border border-white/10 bg-white/[0.07] text-white hover:bg-white/[0.14] hover:text-yellow-200',
          className,
        )}
        aria-label={`Language: ${currentMeta.englishLabel}`}
      >
        <LanguageIcon />
      </IconButton>
    ) : (
      <Button
        variant="text"
        className={clsx(
          'inline-flex items-center gap-6 rounded-full border border-white/10 bg-white/[0.06] px-12 py-6 text-[13px] font-bold tracking-wide text-white/85 transition hover:bg-white/[0.12] hover:text-white',
          className,
        )}
        aria-label={`Language: ${currentMeta.englishLabel}`}
      >
        <span aria-hidden>{currentMeta.flag}</span>
        <span className="hidden md:inline">{currentMeta.label}</span>
      </Button>
    );

  return (
    <MenuTrigger>
      {trigger}
      <Menu>
        {active.map(locale => {
          const meta = LOCALE_META[locale];
          const selected = locale === current;
          return (
            <Item
              key={locale}
              value={locale}
              onSelected={() => switchTo(locale)}
              startIcon={
                <span className="text-base leading-none" aria-hidden>
                  {meta.flag}
                </span>
              }
              description={selected ? '✓' : undefined}
            >
              {meta.label}
            </Item>
          );
        })}
      </Menu>
    </MenuTrigger>
  );
}
